if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false, true));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      static normalizeColorKey(value) {
        return String(value || '')
          .toLowerCase()
          .trim()
          .normalize('NFKD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      static mediaAltMatchesColor(mediaAlt, colorName) {
        const altKey = MediaGallery.normalizeColorKey(mediaAlt);
        const colorKey = MediaGallery.normalizeColorKey(colorName);
        if (!colorKey || !altKey) return false;
        return altKey.includes(colorKey);
      }

      static variantColorMatches(selectedColor, variantColor) {
        const selectedKey = MediaGallery.normalizeColorKey(selectedColor);
        const variantKey = MediaGallery.normalizeColorKey(variantColor);
        if (!selectedKey || !variantKey) return false;
        return selectedKey === variantKey;
      }

      getGalleryConfig() {
        const sectionId = this.id.replace('MediaGallery-', '').replace('-duplicate', '');
        const configScript = document.getElementById(`PdpGalleryConfig-${sectionId}`);
        if (!configScript) return null;

        try {
          return JSON.parse(configScript.textContent);
        } catch (error) {
          return null;
        }
      }

      filterGalleryByColor(activeColor) {
        if (this.dataset.hasColorFilter !== 'true' || !activeColor) return 0;

        const viewer = this.elements.viewer;
        if (!viewer) return 0;

        let visibleCount = 0;

        viewer.querySelectorAll('.product__media-list .product__media-item').forEach((element) => {
          const videoVariantColor = element.getAttribute('data-video-variant-color');
          let matches = false;

          if (videoVariantColor !== null && videoVariantColor !== '') {
            matches = MediaGallery.variantColorMatches(activeColor, videoVariantColor);
          } else {
            matches = MediaGallery.mediaAltMatchesColor(element.getAttribute('data-media-alt'), activeColor);
          }

          element.classList.toggle('not-current-variant', !matches);
          if (matches) visibleCount += 1;
        });

        const thumbnailRoot = this.elements.thumbnails;
        if (thumbnailRoot) {
          thumbnailRoot.querySelectorAll('.thumbnail-list__item').forEach((thumbnail) => {
            const matches = MediaGallery.mediaAltMatchesColor(thumbnail.getAttribute('data-media-alt'), activeColor);
            thumbnail.classList.toggle('not-current-variant', !matches);
          });
        }

        this.classList.toggle('gallery-has-no-media', visibleCount === 0);
        return visibleCount;
      }

      clearActiveSlide() {
        this.elements.viewer?.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('button').forEach((element) => {
          element.removeAttribute('aria-current');
        });
      }

      getFirstVisibleSlide() {
        return this.elements.viewer?.querySelector(
          '.product__media-list .product__media-item:not(.not-current-variant)'
        );
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, shouldScroll, activeColor) {
        const galleryConfig = this.getGalleryConfig();
        const colorName = activeColor || galleryConfig?.activeColor || '';

        const visibleCount = this.filterGalleryByColor(colorName);

        let activeMedia = null;
        if (visibleCount > 0) {
          activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
          if (activeMedia?.classList.contains('not-current-variant')) {
            activeMedia = this.getFirstVisibleSlide();
          }
        }

        this.clearActiveSlide();
        activeMedia?.classList.add('is-active');

        if (prepend && activeMedia) {
          activeMedia.classList.remove('product__media-item--variant');
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${activeMedia.dataset.mediaId}"]`
            );
            activeThumbnail?.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails && activeMedia) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            if (shouldScroll && activeMedia) {
              activeMedia.scrollIntoView({ behavior: 'smooth' });
            }
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return activeMedia;
        const activeThumbnail = activeMedia
          ? this.elements.thumbnails.querySelector(`[data-target="${activeMedia.dataset.mediaId}"]`)
          : null;
        this.setActiveThumbnail(activeThumbnail);
        if (activeMedia && activeThumbnail) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset.mediaIndex || activeThumbnail.dataset.mediaPosition
          );
        }

        return activeMedia;
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem?.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
